/**
 * Single source of truth for the auth bearer token. The http client attaches
 * it to every request; a 401 clears it and bounces to the login screen. A real
 * login flow (out of scope here) writes the token via `setAuthToken`.
 *
 * Kept in `localStorage` deliberately — it must survive a full-page redirect to
 * `/login` and back, and it is read synchronously by the request interceptor.
 */
const AUTH_TOKEN_KEY = 'auth_token';
/** Refresh token, stored alongside the access token (see `AUTH_TOKEN_KEY`). */
const REFRESH_TOKEN_KEY = 'refresh_token';

/** Path the app redirects to on an unauthorized response. */
export const LOGIN_PATH = '/login';

/**
 * Token issued ONLY by the mock (MSW) login handler. It can never authorize
 * against a real backend — any live boot holding it must drop it (see the
 * cpanel `dropStaleMockToken` guard) instead of replaying it into a doomed
 * `401`.
 */
export const MOCK_ADMIN_TOKEN = 'mock-admin-token';

export function readAuthToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAuthToken(token: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function readRefreshToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setRefreshToken(token: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export function clearAuthToken(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Read the `exp` claim (seconds since epoch) off a JWT access token WITHOUT
 * verifying it — expiry is a client-side freshness hint, not a security
 * decision; the server re-checks on every call.
 *
 * Returns `null` when the token is not a decodable JWT (e.g. the mock token)
 * or carries no numeric `exp`. Never throws: undecodable input yields `null`,
 * and callers treat that as "unknown, use as-is" — the server decides.
 */
export function readTokenExpiry(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    if (!payload) return null;
    const base64 = payload.replaceAll('-', '+').replaceAll('_', '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes)) as { exp?: unknown };
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch {
    return null;
  }
}

/**
 * Single-flight guard: many in-flight requests can 401 at once when a token
 * expires. Only the first one may clear the token and kick off the redirect;
 * the rest are no-ops while the page unloads. Module-level on purpose — the
 * full-page `location.replace` resets it naturally on the next load.
 */
let redirectingToLogin = false;

/**
 * Handle an unauthorized (401) response globally: drop the stale token and send
 * the user to login exactly once (single-flight — concurrent 401s are no-ops).
 * Guarded so we never loop when already on the login page, and a no-op outside
 * the browser (tests, SSR).
 */
export function handleUnauthorized(): void {
  if (redirectingToLogin) return;
  clearAuthToken();
  if (typeof window === 'undefined') return;
  if (window.location.pathname === LOGIN_PATH) return;
  redirectingToLogin = true;
  window.location.replace(LOGIN_PATH);
}

/**
 * Shared in-flight refresh attempt: concurrent 401s must trigger exactly one
 * `POST auth/refresh` and all await its result, rather than each racing its
 * own refresh (which would invalidate the others' tokens). `refresher` is
 * supplied by the http client (which owns the base URL / request plumbing) —
 * this module only coordinates single-flight and storage.
 */
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Attempt one token refresh, sharing the result across concurrent callers.
 * Returns the new access token on success, or `null` if there is no refresh
 * token or the refresh call itself fails — either way the caller should fall
 * back to `handleUnauthorized()`.
 */
export function refreshAuthToken(
  refresher: (
    refreshToken: string,
  ) => Promise<{ accessToken: string; refreshToken: string }>,
): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = readRefreshToken();
  if (refreshToken === null) return Promise.resolve(null);

  refreshInFlight = refresher(refreshToken)
    .then((tokens) => {
      // Guard the storage write: persisting a non-string would stringify to
      // `"undefined"` and every later request would send `Bearer undefined`.
      // Treated as a failed refresh instead, so the caller falls back to login.
      if (
        typeof tokens.accessToken !== 'string' ||
        typeof tokens.refreshToken !== 'string'
      ) {
        return null;
      }
      setAuthToken(tokens.accessToken);
      setRefreshToken(tokens.refreshToken);
      return tokens.accessToken;
    })
    .catch(() => null)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}
